import { Controller } from "@hotwired/stimulus"

// Base context-menu primitive.
//
// Owns the generic mechanics of a right-click (or programmatically triggered)
// context menu rendered as a fixed overlay:
//   - contextmenu interception + viewport-aware positioning
//   - open/close lifecycle
//   - Escape / click-away / re-contextmenu close listeners
//   - Turbo-frame content loading with a loading -> content swap
//
// Subclasses override the hooks below to supply feature-specific behaviour.
// The menu content itself is always server-rendered through the Turbo frame to
// follow the "no HTML in JS" convention.
//
// IMPORTANT (Stimulus subclassing): `static targets` arrays merge up the
// prototype chain, but the DOM target attributes are scoped to the *registered
// identifier of the concrete controller* (e.g. data-image-context-menu-target),
// not "context-menu". A subclass that wants to use the menu / loading / content
// / frame targets must therefore still declare them in its own `static targets`
// (or merge them — see the album subclass) and the markup must use the concrete
// controller's identifier. This base references those targets defensively with
// hasXxxTarget guards so it stays safe regardless of what a subclass declares.
export default class extends Controller {
  static targets = ["menu", "frame", "loading", "content"]

  connect() {
    this.boundClose = this.close.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.removeListeners()
  }

  // --- Open lifecycle ---------------------------------------------------

  // Default open handler. Intercepts the contextmenu event, resolves the
  // triggering element, positions the menu, and loads frame content.
  // Subclasses usually keep this and override the hooks instead.
  open(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.beforeOpen(event)) return

    // Stamp the open time so the click-away handler can ignore the opening
    // gesture's own trailing click (see addListeners). A Magic Mouse
    // secondary-click emits a trailing `click` event AFTER the rAF below adds
    // the document click listener, which would otherwise close the menu the
    // instant it opens.
    this.openedAt = Date.now()

    this.showLoading()
    this.positionMenu(event.clientX, event.clientY)
    if (this.hasMenuTarget) this.menuTarget.classList.remove("hidden")

    this.loadFrame()

    requestAnimationFrame(() => this.addListeners())

    this.afterOpen(event)
  }

  // --- Subclass hooks ---------------------------------------------------

  // Return true to proceed with opening, false to abort. Subclasses resolve
  // the triggering context (e.g. which image was clicked) here and stash any
  // state they need. Default: always open.
  beforeOpen(event) {
    return true
  }

  // The Turbo-frame `src` to load. Subclasses MUST override this to return the
  // server endpoint that renders their menu content. Returning a falsy value
  // skips frame loading (e.g. a menu whose content is already static).
  frameSrc() {
    return null
  }

  // Called after the menu has been positioned, shown, and frame load kicked
  // off. Subclasses override for any post-open behaviour. Default: no-op.
  afterOpen(event) {}

  // --- Frame loading ----------------------------------------------------

  loadFrame() {
    const src = this.frameSrc()
    if (!src || !this.hasFrameTarget) return

    // Re-fetch on every open so the menu always reflects current server state
    // (review status, album membership). Wire the loading->content swap first
    // so it fires whether we reload an unchanged src or set a new one.
    this.frameTarget.addEventListener(
      "turbo:frame-load",
      this.handleFrameLoad.bind(this),
      { once: true }
    )

    const current = this.frameTarget.getAttribute("src")
    if (current === src) {
      // src unchanged — Turbo won't re-fetch on assignment, so force it.
      this.frameTarget.reload()
    } else {
      this.frameTarget.src = src
    }
  }

  handleFrameLoad() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasContentTarget) this.contentTarget.classList.remove("hidden")
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasContentTarget) this.contentTarget.classList.add("hidden")
  }

  // --- Close lifecycle --------------------------------------------------

  close() {
    if (this.justDragged) return
    if (this.hasMenuTarget) this.menuTarget.classList.add("hidden")
    this.dragEnd()
    this.removeListeners()
  }

  addListeners() {
    this.boundClickOutside = (event) => {
      if (this.hasMenuTarget && this.menuTarget.contains(event.target)) return
      // Open guard: ignore outside clicks within 300ms of opening. A Magic
      // Mouse secondary-click emits a trailing `click` (within tens of ms of
      // the contextmenu event) that fires after this rAF-deferred listener is
      // attached — that click is the opening gesture, not a deliberate
      // click-away, so it must not close the menu. A genuine click-away always
      // lands well after this window.
      if (Date.now() - this.openedAt < 300) return
      this.close()
    }
    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("contextmenu", this.boundClose)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  removeListeners() {
    if (this.boundClickOutside) {
      document.removeEventListener("click", this.boundClickOutside)
      this.boundClickOutside = null
    }
    document.removeEventListener("contextmenu", this.boundClose)
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  // --- Drag support (header acts as drag handle) ----------------------------

  dragStart(event) {
    if (!this.hasMenuTarget) return
    event.preventDefault()
    event.stopPropagation()
    const rect = this.menuTarget.getBoundingClientRect()
    this.dragOffsetX = event.clientX - rect.left
    this.dragOffsetY = event.clientY - rect.top
    this.boundDragMove = this.dragMove.bind(this)
    this.boundDragEnd  = this.dragEnd.bind(this)
    document.addEventListener("mousemove", this.boundDragMove)
    document.addEventListener("mouseup",   this.boundDragEnd)
  }

  dragMove(event) {
    if (!this.hasMenuTarget) return
    const left = Math.max(0, Math.min(event.clientX - this.dragOffsetX, window.innerWidth  - 60))
    const top  = Math.max(0, Math.min(event.clientY - this.dragOffsetY, window.innerHeight - 60))
    this.menuTarget.style.left = `${left}px`
    this.menuTarget.style.top  = `${top}px`
  }

  dragEnd() {
    if (this.boundDragMove) {
      document.removeEventListener("mousemove", this.boundDragMove)
      document.removeEventListener("mouseup",   this.boundDragEnd)
      this.boundDragMove = null
      this.boundDragEnd  = null
    }
    this.justDragged = true
    setTimeout(() => { this.justDragged = false }, 100)
  }

  // --- Positioning ------------------------------------------------------

  // Position the menu at (x, y), nudging it back inside the viewport if it
  // would overflow the right or bottom edge.
  positionMenu(x, y) {
    if (!this.hasMenuTarget) return

    const menu = this.menuTarget
    // Temporarily show (invisible) to measure dimensions.
    menu.style.visibility = "hidden"
    menu.classList.remove("hidden")

    const menuRect = menu.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = x
    if (x + menuRect.width > viewportWidth - 8) {
      left = x - menuRect.width
    }
    left = Math.max(8, left)

    let top = y
    if (y + menuRect.height > viewportHeight - 8) {
      top = y - menuRect.height
    }
    top = Math.max(8, top)

    menu.style.left = `${left}px`
    menu.style.top = `${top}px`
    menu.style.visibility = "visible"
  }
}
